import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';

import { rowName, dayOf, useSinceMoves, readingNote } from './priceArchive';
import ItemDetailModal, { DetailButton, TradeButton, hasTrade } from './ItemDetailModal';
import { findSkinImage } from './skinIndex';
import { buildStickerIndex, withStickerImages } from './helpers';
import StickerSlots from './StickerSlots';

const SORTS = [
  { key: 'date',   label: 'Newest first' },
  { key: 'profit', label: 'Best profit' },
  { key: 'loss',   label: 'Worst profit' },
  { key: 'roi',    label: 'Best ROI' },
];

const FILTERS = [
  { key: 'all',  label: 'All' },
  { key: 'win',  label: 'Wins' },
  { key: 'loss', label: 'Losses' },
];

// The day the money landed. A sale reconciled from CSFloat carries its own
// timestamp; a hand-entered one only ever has `timestamp`, which is the date
// this page already prints on the ticket.
const saleDay = row => dayOf(row?.csfloatSale?.soldAt || row?.soldAt || row?.timestamp);

export default function SoldTab3({ rows, settings, skinsData = [], livePrices, csfloatPrices, onEdit }) {
  const [query, setQuery]   = useState('');
  const [sort, setSort]     = useState('date');
  const [outcome, setOutcome] = useState('all');
  const [detail, setDetail] = useState(null);   // the ticket whose modal is open

  // Sticker pictures: the row stores names, the catalogue has the art.
  const stickerIndex = useMemo(() => buildStickerIndex(skinsData), [skinsData]);

  const sold = useMemo(() => {
    return (rows || [])
      .map((row, realIndex) => ({ row, realIndex }))
      .filter(({ row }) => row.skin?.value && parseFloat(row.sellPrice) > 0)
      .map(e => {
        const buy    = parseFloat(e.row.buyPrice) || 0;
        const sell   = parseFloat(e.row.sellPrice) || 0;
        const profit = parseFloat(e.row.profit) || 0;
        const roi    = buy > 0 ? profit / buy * 100 : null;
        return { ...e, buy, sell, profit, roi, ts: new Date(e.row.timestamp) };
      });
  }, [rows]);

  // ── Two questions the ticket could never answer ───────────
  // The archive prices the market on the day each sale closed, which splits
  // "was that a good sale" into the two halves it has always been:
  //
  //   execution  what you got against what the market wanted THAT DAY
  //   timing     what the market has done since
  //
  // Neither is in the profit figure, and neither was answerable before: the
  // row's own snapshots stop the day it sold.
  const archivePoints = useMemo(() => sold.map(e => ({
    key:  e.realIndex,
    name: rowName(e.row),
    date: saleDay(e.row),
  })), [sold]);
  const archive = useSinceMoves(archivePoints, csfloatPrices);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = sold.filter(e => {
      if (q && !e.row.skin.value.toLowerCase().includes(q)) return false;
      if (outcome === 'win'  && e.profit < 0) return false;
      if (outcome === 'loss' && e.profit >= 0) return false;
      return true;
    });
    const by = {
      date:   (a, b) => b.ts - a.ts,
      profit: (a, b) => b.profit - a.profit,
      loss:   (a, b) => a.profit - b.profit,
      roi:    (a, b) => (b.roi ?? -Infinity) - (a.roi ?? -Infinity),
    }[sort];
    return [...list].sort(by);
  }, [sold, query, sort, outcome]);

  // Lifetime figures for the headline row.
  const totals = useMemo(() => {
    const profit  = sold.reduce((s, e) => s + e.profit, 0);
    const revenue = sold.reduce((s, e) => s + e.sell, 0);
    const cost    = sold.reduce((s, e) => s + e.buy, 0);
    const wins    = sold.filter(e => e.profit >= 0).length;

    // Execution and timing, summed over every sale the archive can price.
    const market = sold.reduce((acc, e) => {
      const mv = archive.get(e.realIndex);
      if (!mv) return acc;
      return {
        n: acc.n + 1,
        edge:  acc.edge + (e.sell - mv.then),   // you vs the market that day
        drift: acc.drift + mv.change,           // the market since that day
        base:  acc.base + mv.then,
        early: acc.early + (mv.change > 0 ? 1 : 0),
      };
    }, { n: 0, edge: 0, drift: 0, base: 0, early: 0 });

    return {
      profit, revenue, cost, wins, losses: sold.length - wins, market,
      driftPct: market.base > 0 ? (market.drift / market.base) * 100 : null,
      winRate: sold.length ? Math.round(wins / sold.length * 100) : null,
      roi: cost > 0 ? profit / cost * 100 : null,
    };
  }, [sold, archive]);

  // Scoped to the current filter, for the toolbar.
  const shown = useMemo(() => {
    const profit = filtered.reduce((s, e) => s + e.profit, 0);
    const wins   = filtered.filter(e => e.profit >= 0).length;
    return { profit, wins, losses: filtered.length - wins };
  }, [filtered]);

  const profC = settings?.profitTextColor || 'var(--green)';
  const lossC = settings?.lossTextColor   || 'var(--red)';

  if (!sold.length) {
    return (
      <div className="sd3-root">
        <div className="lv2-empty">No sales recorded yet — sold items become trade tickets here.</div>
      </div>
    );
  }

  return (
    <div className="sd3-root">

      {/* ── Headline totals ────────────────────────── */}
      <div className="pt-hero">
        <div className="pt-hero-cell">
          <div className="k">Banked profit</div>
          <div className="v" style={{ color: totals.profit >= 0 ? profC : lossC }}>
            {totals.profit >= 0 ? '+' : '−'}${Math.abs(totals.profit).toFixed(2)}
          </div>
          <div className="s">
            {totals.roi != null
              ? `${totals.roi >= 0 ? '+' : '−'}${Math.abs(totals.roi).toFixed(1)}% on $${totals.cost.toFixed(2)} spent`
              : 'n/a'}
          </div>
        </div>
        <div className="pt-hero-cell">
          <div className="k">Revenue</div>
          <div className="v">${totals.revenue.toFixed(2)}</div>
          <div className="s">across {sold.length} sale{sold.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="pt-hero-cell">
          <div className="k">Win rate</div>
          <div className="v">{totals.winRate == null ? 'n/a' : `${totals.winRate}%`}</div>
          <div className="s">{totals.wins} of {sold.length} sales made money</div>
        </div>
        {/* Both of these need the price archive — the row stops recording the
            day it sells, so nothing in the app knew what the market did next.
            Hidden entirely when no sale could be priced. */}
        {totals.market.n > 0 && (
          <div className="pt-hero-cell">
            <div className="k">Sold vs market</div>
            <div className="v" style={{ color: totals.market.edge >= 0 ? profC : lossC }}>
              {totals.market.edge >= 0 ? '+' : '−'}${Math.abs(totals.market.edge).toFixed(2)}
            </div>
            <div className="s">
              what you got against the CSFloat price on each sale day
              {totals.market.n < sold.length ? ` · ${totals.market.n} of ${sold.length} priced` : ''}
            </div>
          </div>
        )}
        {totals.market.n > 0 && (
          <div className="pt-hero-cell">
            <div className="k">Market since you sold</div>
            <div className="v" style={{ color: totals.market.drift <= 0 ? profC : lossC }}>
              {totals.market.drift >= 0 ? '+' : '−'}${Math.abs(totals.market.drift).toFixed(2)}
              {totals.driftPct != null && (
                <span style={{ fontSize: '0.92rem', fontWeight: 700 }}>
                  {' '}({totals.driftPct >= 0 ? '+' : '−'}{Math.abs(totals.driftPct).toFixed(1)}%)
                </span>
              )}
            </div>
            <div className="s">
              {totals.market.drift > 0
                ? `${totals.market.early} of ${totals.market.n} would be worth more today`
                : 'holding would have left you worse off'}
            </div>
          </div>
        )}
      </div>

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="lv2-bar" style={{ position: 'static', marginBottom: 16 }}>
        <div className="lv2-search">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
          </svg>
          <input placeholder="Search tickets…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        {FILTERS.map(f => (
          <button key={f.key} className={`lv2-chip ${outcome === f.key ? 'on' : ''}`}
            onClick={() => setOutcome(f.key)}>
            {f.label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: '0.92rem', color: 'var(--text-dim)', fontWeight: 600 }}>
          {filtered.length} ticket{filtered.length !== 1 ? 's' : ''} · {shown.wins}W/{shown.losses}L ·{' '}
          <span style={{ color: shown.profit >= 0 ? profC : lossC }}>
            {shown.profit >= 0 ? '+' : '−'}${Math.abs(shown.profit).toFixed(2)}
          </span>
        </span>
        <select className="lv2-select" value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort tickets">
          {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {/* ── Tickets ────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="lv2-empty">No tickets match your filters.</div>
      ) : (
        <div className="sd3-grid">
          {filtered.map((e, i) => {
            const img = findSkinImage(skinsData, e.row.skin.value, e.row.condition?.value);
            const win = e.profit >= 0;
            return (
              <motion.div
                key={e.realIndex}
                className="sd3-ticket"
                onClick={() => onEdit && onEdit(e.row, e.realIndex)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 18) * 0.03 }}
              >
                <div className="sd3-main">
                  <DetailButton onOpen={() => setDetail({ row: e.row, img })} />
                  {/* Only on items with a trade behind them: the card click opens
                      the editor, so this is the way in to the trade overview. */}
                  {hasTrade(e.row) && (
                    <TradeButton className="" onOpen={() => setDetail({ row: e.row, img })} />
                  )}
                  <div className="sd3-date">
                    {e.ts.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    {e.row.siteFee?.label ? ` · ${e.row.siteFee.label.split(' ')[0]}` : ''}
                  </div>
                  <div className="sd3-body">
                    {img
                      ? <img src={img} alt="" />
                      : <div style={{ width: 58, height: 38, background: 'rgba(255,255,255,0.04)', borderRadius: 6, flexShrink: 0 }} />}
                    <div style={{ minWidth: 0 }}>
                      <div className="sd3-name" title={e.row.skin.value}>{e.row.skin.value}</div>
                      <div className="sd3-sub">
                        {e.row.condition?.value || '—'}
                        {e.row.receivedFromTradeId ? ' · from trade' : ''}
                      </div>
                      {/* What the market has done since this one closed. Green
                          when selling was the right call, red when it was not
                          — the opposite polarity to the profit stub, because a
                          rising market after a sale is money you did not get. */}
                      {(() => {
                        const mv = archive.get(e.realIndex);
                        if (!mv) return null;
                        return (
                          <div className="sd3-sub" title={readingNote(mv)}
                            style={{ color: mv.change <= 0 ? profC : lossC }}>
                            market {mv.change >= 0 ? '+' : '−'}${Math.abs(mv.change).toFixed(2)}
                            {' '}({mv.pct >= 0 ? '+' : '−'}{Math.abs(mv.pct).toFixed(1)}%) since · was ${mv.then.toFixed(2)}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  {/* What was on it when it sold. Unlike the Live cards, a
                      ticket with no stickers draws nothing rather than holding
                      the row open: most sales are plain items, and a column of
                      empty strips would be all the eye sees. */}
                  <StickerSlots
                    stickers={withStickerImages(e.row.stickers, stickerIndex)}
                    charms={e.row.charms}
                    prices={livePrices} variant="classic" maxWidth={176}
                    style={{ marginTop: 10, marginBottom: 0 }} />

                  <div className="sd3-route">
                    <b>${e.buy.toFixed(2)}</b>
                    <span className="track" />
                    <b>${e.sell.toFixed(2)}</b>
                  </div>
                </div>

                <div className="sd3-perf" aria-hidden="true" />

                <div className="sd3-stub">
                  <span className={`sd3-stamp ${win ? 'win' : 'loss'}`}>{win ? 'WIN' : 'LOSS'}</span>
                  <div className="sd3-profit" style={{ color: win ? profC : lossC }}>
                    {win ? '+' : '−'}${Math.abs(e.profit).toFixed(2)}
                  </div>
                  {e.roi != null && (
                    <div className="sd3-roi" style={{ color: win ? profC : lossC }}>
                      {win ? '+' : '−'}{Math.abs(e.roi).toFixed(1)}%
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {detail && (
        <ItemDetailModal
          skinsData={skinsData}
          variant="classic"
          kind="sold"
          name={detail.row.skin?.value}
          condition={detail.row.condition?.value}
          image={detail.img}
          history={detail.row.priceHistory}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          row={detail.row}
          rows={rows || []}
          /* click-through: any item in the trade chain re-centres this modal */
          onOpenRow={r => setDetail({ row: r, img: findSkinImage(skinsData, r.skin?.value, r.condition?.value) })}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
